import { Rng } from '../core/rng';
import { Quat, TAU, Vec3 } from '../core/math';
import { makeWaveSample, type WaveField } from './waves';
import type { Wind } from './wind';

// Things riding the sea around the ship: flotsam near by, and other sail on
// the horizon. Both follow the same CPU wave field as the ship.

export type FlotsamKind = 'plank' | 'barrel' | 'crate' | 'spar' | 'weed' | 'canvas';

export interface Floater {
  kind: FlotsamKind;
  pos: Vec3;
  rot: Quat;
  yaw: number;
  yawRate: number;
  size: number;
  /** Smoothed surface normal. */
  normal: Vec3;
  /** Wreckage stays where it sank (no respawning); seconds left afloat. */
  wreck?: number;
}

export interface Sail {
  pos: Vec3;
  rot: Quat;
  heading: number;
  speed: number;
  kind: 'brig' | 'galleon' | 'sloop';
  scale: number;
  pitch: number;
  roll: number;
}

const sample = makeWaveSample();
const FLOTSAM_KINDS: readonly FlotsamKind[] = ['plank', 'plank', 'barrel', 'crate', 'spar', 'weed', 'weed'];

export class Flotsam {
  readonly items: Floater[] = [];

  constructor(private readonly rng: Rng, count: number, shipX: number, shipZ: number) {
    for (let i = 0; i < count; i++) {
      const kind = this.rng.pick(FLOTSAM_KINDS);
      const f: Floater = {
        kind, pos: new Vec3(), rot: new Quat(), yaw: this.rng.range(0, TAU), yawRate: this.rng.range(-0.08, 0.08),
        size: this.rng.range(0.8, 1.3), normal: new Vec3(0, 1, 0),
      };
      this.place(f, shipX, shipZ, 30, 380, 0, 0);
      this.items.push(f);
    }
  }

  private place(f: Floater, sx: number, sz: number, rMin: number, rMax: number, hx: number, hz: number): void {
    // Bias new flotsam ahead of the ship so it drifts into view.
    let a = this.rng.range(0, TAU);
    if (hx !== 0 || hz !== 0) a = Math.atan2(hz, hx) + this.rng.range(-1.1, 1.1);
    const r = this.rng.range(rMin, rMax);
    f.pos.set(sx + Math.cos(a) * r, 0, sz + Math.sin(a) * r);
  }

  /** Wreckage left where a ship went down (or was blown apart). */
  spawnWreck(x: number, z: number, radius: number, count: number): void {
    const kinds: FlotsamKind[] = ['plank', 'plank', 'plank', 'barrel', 'crate', 'spar', 'canvas', 'canvas', 'barrel'];
    for (let i = 0; i < count; i++) {
      const a = this.rng.range(0, TAU), r = Math.sqrt(this.rng.next()) * radius;
      this.items.push({
        kind: this.rng.pick(kinds), pos: new Vec3(x + Math.cos(a) * r, 0, z + Math.sin(a) * r), rot: new Quat(),
        yaw: this.rng.range(0, TAU), yawRate: this.rng.range(-0.15, 0.15), size: this.rng.range(0.7, 1.6), normal: new Vec3(0, 1, 0),
        wreck: this.rng.range(500, 900),
      });
    }
    // Keep the numbers sane.
    while (this.items.length > 220) {
      const i = this.items.findIndex((f) => f.wreck !== undefined);
      if (i < 0) break;
      this.items.splice(i, 1);
    }
  }

  update(dt: number, waves: WaveField, wind: Wind, shipX: number, shipZ: number, shipVx: number, shipVz: number): void {
    const sp = Math.hypot(shipVx, shipVz) || 1;
    for (let i = this.items.length - 1; i >= 0; i--) {
      const f = this.items[i]!;
      if (f.wreck !== undefined) {
        f.wreck -= dt;
        if (f.wreck <= 0) {
          this.items.splice(i, 1);
          continue;
        }
      }
      const dx = f.pos.x - shipX, dz = f.pos.z - shipZ;
      if (f.wreck === undefined && dx * dx + dz * dz > 440 * 440) this.place(f, shipX, shipZ, 300, 420, shipVx / sp, shipVz / sp);
      // Leeway drift: a few percent of the wind plus Stokes drift.
      const k = f.kind === 'weed' ? 0.012 : 0.025;
      f.pos.x += wind.dirX * wind.speed * k * dt;
      f.pos.z += wind.dirZ * wind.speed * k * dt;
      waves.sample(f.pos.x, f.pos.z, sample, false, 2);
      f.pos.y = sample.height + (f.kind === 'barrel' ? 0.05 : f.kind === 'weed' ? 0.02 : 0);
      f.normal.lerp(tmp.set(sample.nx, sample.ny, sample.nz), Math.min(1, dt * 4)).normalize();
      f.yaw += f.yawRate * dt;
      alignToNormal(f.rot, f.normal, f.yaw);
    }
  }
}

export class Traffic {
  readonly ships: Sail[] = [];

  constructor(private readonly rng: Rng, shipX: number, shipZ: number) {
    const kinds: Sail['kind'][] = ['galleon', 'brig', 'sloop'];
    for (let i = 0; i < 3; i++) {
      const s: Sail = {
        pos: new Vec3(), rot: new Quat(), heading: 0, speed: 0, kind: kinds[i]!,
        scale: kinds[i] === 'galleon' ? 1.15 : kinds[i] === 'brig' ? 0.85 : 0.62, pitch: 0, roll: 0,
      };
      this.spawn(s, shipX, shipZ, i);
      this.ships.push(s);
    }
  }

  private spawn(s: Sail, x: number, z: number, i: number): void {
    const a = this.rng.range(0, TAU) + i * 2.1;
    const r = this.rng.range(1800, 4200);
    s.pos.set(x + Math.cos(a) * r, 0, z + Math.sin(a) * r);
    s.heading = this.rng.range(0, TAU);
    s.speed = this.rng.range(2.2, 4.2);
  }

  update(dt: number, waves: WaveField, shipX: number, shipZ: number): void {
    this.ships.forEach((s, i) => {
      const dx = s.pos.x - shipX, dz = s.pos.z - shipZ;
      if (dx * dx + dz * dz > 6500 * 6500) this.spawn(s, shipX, shipZ, i);
      const fx = Math.sin(s.heading), fz = -Math.cos(s.heading);
      s.pos.x += fx * s.speed * dt;
      s.pos.z += fz * s.speed * dt;
      const L = 16 * s.scale;
      const hb = waves.heightAt(s.pos.x + fx * L, s.pos.z + fz * L);
      const hs = waves.heightAt(s.pos.x - fx * L, s.pos.z - fz * L);
      const hp = waves.heightAt(s.pos.x + fz * 5, s.pos.z - fx * 5);
      const hsb = waves.heightAt(s.pos.x - fz * 5, s.pos.z + fx * 5);
      const target = (hb + hs + hp + hsb) / 4;
      s.pos.y += (target - s.pos.y) * Math.min(1, dt * 1.5);
      s.pitch += (Math.atan2(hb - hs, 2 * L) - s.pitch) * Math.min(1, dt * 1.2);
      s.roll += (Math.atan2(hp - hsb, 10) * 0.8 + 0.06 - s.roll) * Math.min(1, dt * 1.0);
      s.rot.setFromYawPitchRoll(Math.PI / 2 - s.heading, s.pitch, s.roll);
    });
  }
}

const tmp = new Vec3();
const tq = new Quat();

/** Orientation with local +Y along n and yaw about it. */
function alignToNormal(out: Quat, n: Vec3, yaw: number): void {
  // Rotation taking +Y to n.
  const ax = n.z, az = -n.x;
  const s = Math.hypot(ax, az);
  if (s < 1e-6) out.set(0, 0, 0, 1);
  else out.setFromAxisAngle(ax / s, 0, az / s, Math.acos(Math.max(-1, Math.min(1, n.y))));
  out.multiply(tq.setFromAxisAngle(0, 1, 0, yaw));
}
